import React, { useState, useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuthStore, useThemeStore } from '../stores'
import { Avatar } from './ui/avatar'
import { Button } from './ui/button'
import { 
  Menu, 
  X, 
  Calendar, 
  Users, 
  LogOut, 
  Moon, 
  Sun,
  Radio,
  UserCog,
  ShieldCheck,
  Boxes
} from 'lucide-react'
import { cn } from '../lib/utils'

interface LayoutProps {
  children: React.ReactNode
  title?: string
  description?: string
  showSidebar?: boolean
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  title, 
  description, 
  showSidebar = true 
}) => {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const refreshUser = useAuthStore((state) => state.refreshUser)
  const darkMode = useThemeStore((state) => state.darkMode)
  const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const hasAttemptedRefresh = useRef(false)

  // Auto-refresh user data if role/permissions are missing (only once)
  useEffect(() => {
    if (user && (!user.role || !user.role.permissions) && !hasAttemptedRefresh.current) {
      hasAttemptedRefresh.current = true;
      refreshUser();
    }
  }, [user, refreshUser])

  const hasPermission = (permission?: string) => {
    if (!permission) return true;
    if (!user || !user.role || !user.role.permissions) {
      return false;
    }
    return user.role.permissions.includes(permission);
  };

  const navigationGroups = [
    {
      name: 'Services',
      items: [
        { name: 'Meetings', href: '/dashboard/meetings', icon: Calendar },
        { name: 'Live Streams', href: '/dashboard/livestreams', icon: Radio }
      ]
    },
    {
      name: 'Integrations',
      items: [
        { name: 'Applications', href: '/dashboard/apps', icon: Boxes }
      ]
    },
    {
      name: 'Administration',
      items: [
        { name: 'Users', href: '/dashboard/users', icon: Users, permission: 'users.manage' as string | undefined },
        { name: 'Roles', href: '/dashboard/roles', icon: ShieldCheck, permission: 'users.manage' as string | undefined }
      ]
    }
  ].map(group => ({
    ...group,
    items: group.items.filter((item: any) => hasPermission(item.permission))
  })).filter(group => group.items.length > 0)

  return (
    <div className="min-h-screen bg-background theme-transition">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm lg:hidden" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      {showSidebar && (
        <aside 
          className={cn(
            "fixed bottom-0 top-0 inset-y-0 left-0 z-50 w-64 sm:w-72 lg:w-64 transform bg-card border-r border-border transition-transform duration-300 ease-in-out lg:translate-x-0  lg:inset-0 theme-transition",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
          aria-label="Main navigation"
        >
          <div className="flex h-full flex-col">
            {/* Sidebar header */}
            <div className="flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6 border-b border-border">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <img 
                  src="/logo.svg" 
                  alt="LiveKit Logo" 
                  className="h-7 w-7 sm:h-8 sm:w-8"
                />
                <h1 className="text-base sm:text-lg font-semibold text-foreground">LiveKit</h1>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden touch-target"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close navigation menu"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>

            {/* Navigation */}
            <nav id="sidebar-navigation" className="flex-1 px-3 sm:px-4 py-3 sm:py-4" role="navigation" aria-label="Main menu">
              {navigationGroups.map((group, groupIndex) => (
                <div key={group.name} className={groupIndex > 0 ? "mt-6" : "mt-4"}>
                  {/* Group Header */}
                  <h2 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {group.name}
                  </h2>
                  
                  {/* Group Items */}
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const Icon = item.icon
                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            cn(
                              "group flex items-center px-3 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors touch-target",
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            )
                          }
                        >
                          <Icon className="mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" aria-hidden="true" />
                          <span className="truncate">{item.name}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* User profile */}
            <div className="border-t border-border p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Avatar
                  fallback={user ? `${user.first_name} ${user.last_name}`.trim().charAt(0).toUpperCase() || 'U' : 'U'}
                  className="h-8 w-8 sm:h-10 sm:w-10"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user ? `${user.first_name} ${user.last_name}`.trim() : 'Anonymous'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate hidden sm:block">
                    {user?.email}
                  </p>
                </div>
                <NavLink to="/dashboard/profile">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit Profile"
                    className="touch-target flex-shrink-0"
                  >
                    <UserCog className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Main content */}
      <div className={cn("flex flex-col top-0 left-0 right-0 absolute", showSidebar && "lg:ml-64")}>
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border theme-transition">
          <div className="flex h-14 sm:h-16 items-center gap-2 sm:gap-4 px-4 sm:px-6 lg:px-8">
            {showSidebar && (
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden touch-target"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={sidebarOpen}
                aria-controls="sidebar-navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}

            {/* Page title */}
            <div className="flex-1 min-w-0">
              {title && (
                <div>
                  <h1 className="text-base sm:text-lg font-semibold text-foreground truncate">{title}</h1>
                  {description && (
                    <p className="text-xs sm:text-sm text-muted-foreground truncate hidden sm:block">{description}</p>
                  )}
                </div>
              )}
            </div>

            {/* Header actions */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* Search - desktop only */}
              {/* <Button 
                variant="ghost" 
                size="icon" 
                className="hidden sm:flex"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </Button> */}
              
              {/* Notifications */}
              {/* <div className="relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="touch-target"
                  aria-label="Notifications (3 unread)"
                >
                  <Bell className="h-5 w-5" />
                </Button>
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
                  aria-hidden="true"
                >
                  3
                </Badge>
              </div> */}

              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="touch-target"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* Logout button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="touch-target"
                aria-label="Sign out"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  )
}

export default Layout